import { AxiosResponse } from "axios";
import { useQuery } from "react-query";
import { getApiData } from "./helpers";
import { LookupOption, OrderAction, SupplyOrder } from "./types";
import RequisitionType from "Services/Requisition/RequisitionTypeService";
import SupplyManagmentService from "Services/SupplyManagement/SupplyManagmentService";
import UserManagementService from "Services/UserManagement/UserManagementService";

const defaultQueryOptions = {
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
};

const lookupStaleTime = 5 * 60 * 1000;

const getLookupArray = (response: AxiosResponse<any>) => {
  const data = getApiData(response);
  if (Array.isArray(data)) return data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
};

export const oncologySupplyQueryKeys = {
  all: ["oncology-supply-management"],
  facilities: () => [...oncologySupplyQueryKeys.all, "facilities"],
  states: () => [...oncologySupplyQueryKeys.all, "states"],
  actions: () => [...oncologySupplyQueryKeys.all, "actions"],
  printers: () => [...oncologySupplyQueryKeys.all, "printers"],
  facility: (facilityId?: number | string | null) => [
    ...oncologySupplyQueryKeys.all,
    "facility",
    facilityId ?? "empty",
  ],
  orders: (params: any) => [...oncologySupplyQueryKeys.all, "orders", params],
  ordersRoot: () => [...oncologySupplyQueryKeys.all, "orders"],
  order: (orderId?: number | string | null) => [
    ...oncologySupplyQueryKeys.all,
    "order",
    orderId ?? "empty",
  ],
  supplyItems: (itemType?: string | null) => [
    ...oncologySupplyQueryKeys.all,
    "supply-items",
    itemType ?? "empty",
  ],
  itemDescription: (itemId?: number | string | null) => [
    ...oncologySupplyQueryKeys.all,
    "item-description",
    itemId ?? "empty",
  ],
};

export const useOncologyFacilitiesLookup = () =>
  useQuery<AxiosResponse<any>, Error, LookupOption[]>(
    oncologySupplyQueryKeys.facilities(),
    UserManagementService.GetFacilitiesLookup,
    {
      ...defaultQueryOptions,
      staleTime: lookupStaleTime,
      select: (response) => getLookupArray(response),
    }
  );

export const useOncologyStatesLookup = () =>
  useQuery<AxiosResponse<any>, Error, LookupOption[]>(
    oncologySupplyQueryKeys.states(),
    SupplyManagmentService.getStatesLookup,
    {
      ...defaultQueryOptions,
      staleTime: lookupStaleTime,
      select: (response) => getLookupArray(response),
    }
  );

export const useOncologyOrderActionLookup = () =>
  useQuery<AxiosResponse<any>, Error, OrderAction[]>(
    oncologySupplyQueryKeys.actions(),
    SupplyManagmentService.getOrderActionLookup,
    {
      ...defaultQueryOptions,
      staleTime: lookupStaleTime,
      select: (response) => getLookupArray(response),
    }
  );

export const useOncologyPrinters = () =>
  useQuery<AxiosResponse<any>, Error, LookupOption[]>(
    oncologySupplyQueryKeys.printers(),
    RequisitionType.GetPrintersInfo,
    {
      ...defaultQueryOptions,
      staleTime: lookupStaleTime,
      select: (response) => getApiData(response) ?? [],
    }
  );

export const useOncologyFacilityDetail = (facilityId?: number | null) =>
  useQuery<AxiosResponse<any>, Error, any>(
    oncologySupplyQueryKeys.facility(facilityId),
    () => SupplyManagmentService.getFacilityById(facilityId as number),
    {
      ...defaultQueryOptions,
      enabled: !!facilityId,
      staleTime: lookupStaleTime,
      select: (response) => getApiData(response),
    }
  );

export const useOncologyOrders = (params: any) =>
  useQuery<AxiosResponse<any>, Error, { rows: SupplyOrder[]; total: number }>(
    oncologySupplyQueryKeys.orders(params),
    () => SupplyManagmentService.getOrders(params),
    {
      ...defaultQueryOptions,
      keepPreviousData: true,
      select: (response) => ({
        rows: response?.data?.data ?? [],
        total: response?.data?.total ?? 0,
      }),
    }
  );

export const useOncologyOrderDetail = (
  orderId?: number | null,
  enabled = true
) =>
  useQuery<AxiosResponse<any>, Error, SupplyOrder | null>(
    oncologySupplyQueryKeys.order(orderId),
    () => SupplyManagmentService.getOrderById(orderId as number),
    {
      ...defaultQueryOptions,
      enabled: enabled && !!orderId,
      select: (response) => getApiData(response) ?? null,
    }
  );

export const useOncologySupplyItemsLookup = (itemType?: string | null) =>
  useQuery<AxiosResponse<any>, Error, LookupOption[]>(
    oncologySupplyQueryKeys.supplyItems(itemType),
    () => SupplyManagmentService.getSupplyItemsLookup(itemType as string),
    {
      ...defaultQueryOptions,
      enabled: !!itemType,
      staleTime: lookupStaleTime,
      select: (response) => getLookupArray(response),
    }
  );

export const useOncologySupplyItemDescription = (
  itemId?: number | null,
  enabled = true
) =>
  useQuery<AxiosResponse<any>, Error, string>(
    oncologySupplyQueryKeys.itemDescription(itemId),
    () => SupplyManagmentService.getSupplyItemDescriptionById(itemId as number),
    {
      ...defaultQueryOptions,
      enabled: enabled && !!itemId,
      staleTime: lookupStaleTime,
      select: (response) => `${getApiData(response) ?? ""}`,
    }
  );
